import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSubscriptionStats, SubscriptionStats as StatsType, ChartDataPoint } from "@/hooks/useSubscriptionStats";
import { SubscriptionStats } from "@/components/admin/sales/SubscriptionStats";
import { SubscriptionChart } from "@/components/admin/sales/SubscriptionChart";
import { ConversionFunnel } from "@/components/admin/sales/ConversionFunnel";
import { Loader2, RefreshCcw } from "lucide-react";
import { cn } from "@/lib/utils";

export default function AdminSales() {
  const { t } = useTranslation();
  const { getSubscriptionDemographics, getSubscriptionTrends, loading } = useSubscriptionStats();
  const [stats, setStats] = useState<StatsType | null>(null);
  const [trends, setTrends] = useState<ChartDataPoint[]>([]);

  const loadData = useCallback(async () => {
    const [s, t] = await Promise.all([
      getSubscriptionDemographics(),
      getSubscriptionTrends(30)
    ]);
    setStats(s);
    setTrends(t);
  }, [getSubscriptionDemographics, getSubscriptionTrends]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Header Section */}
      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.sales.title')}</h2>
          <p className="text-lg font-medium text-gray-500">{t('admin.sales.subtitle')}</p>
        </div>
        <button 
          onClick={loadData}
          disabled={loading}
          className="rounded-xl border-2 border-black bg-white p-3 shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
        >
          <RefreshCcw className={cn("h-6 w-6", loading && "animate-spin")} />
        </button>
      </div>

      <div className="space-y-8">
        {/* Stats Cards */}
        <SubscriptionStats stats={stats} loading={loading} />

        {/* Charts Section */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 items-stretch">
          <div className="lg:col-span-2 h-full">
            <SubscriptionChart data={trends} />
          </div>
          <div className="h-full">
            <ConversionFunnel 
              free={stats?.free || 0} 
              paid={(stats?.standard || 0) + (stats?.pro || 0)} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}
